import type { TreeData } from '../types';
import type { CSSProperties } from 'react';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

type IndicatorGeometry = { height: number; top: number };
type IndicatorState = {
  geometry: IndicatorGeometry | null;
  shouldAnimate: boolean;
};

function measureActiveRow(root: HTMLElement): IndicatorGeometry | null {
  const row = root.querySelector('[data-tree-viewer-active="true"]');
  if (!row) return null;

  const rootRect = root.getBoundingClientRect();
  const rowRect = row.getBoundingClientRect();

  return {
    height: rowRect.height,
    top: rowRect.top - rootRect.top - root.clientTop,
  };
}

function isSameGeometry(
  a: IndicatorGeometry | null,
  b: IndicatorGeometry | null,
) {
  return a?.top === b?.top && a?.height === b?.height;
}

/**
 * Tracks the vertical position of the active tree row inside the root list.
 *
 * The first position after the indicator appears is applied without animation,
 * and only an active-row change that follows an established position sets
 * `shouldAnimate`. Re-measurements triggered by a size change of the root list
 * never start an animation, and leave an in-progress one running when the
 * measured geometry is unchanged.
 *
 * @param activeAnchorId - Anchor ID of the active row, if any
 * @param treeData - The rendered tree; a new reference re-measures, because
 * replacing or reordering rows can move the active row without resizing the
 * root list
 * @returns `rootRef` for the root list, `indicatorStyle` (undefined while no
 * row is active) and `shouldAnimate`
 */
export function useActiveTreeIndicator(
  activeAnchorId: string | undefined,
  treeData: TreeData,
) {
  const [root, setRoot] = useState<HTMLUListElement | null>(null);
  const [indicator, setIndicator] = useState<IndicatorState>({
    geometry: null,
    shouldAnimate: false,
  });
  const geometryRef = useRef<IndicatorGeometry | null>(null);

  const applyGeometry = useCallback(
    (next: IndicatorGeometry | null, canAnimate: boolean) => {
      // Only a change away from an already-positioned indicator may animate.
      setIndicator({
        geometry: next,
        shouldAnimate:
          canAnimate && next !== null && geometryRef.current !== null,
      });
      geometryRef.current = next;
    },
    [],
  );

  useLayoutEffect(() => {
    // Without a root list there is no geometry, so a remount places the
    // indicator afresh.
    applyGeometry(root ? measureActiveRow(root) : null, true);
  }, [root, activeAnchorId, treeData, applyGeometry]);

  useLayoutEffect(() => {
    if (!root) return;

    const observer = new ResizeObserver(() => {
      const next = measureActiveRow(root);
      if (isSameGeometry(geometryRef.current, next)) return;
      applyGeometry(next, false);
    });
    observer.observe(root);

    return () => observer.disconnect();
  }, [root, applyGeometry]);

  const { geometry, shouldAnimate } = indicator;
  const indicatorStyle: CSSProperties | undefined = geometry
    ? {
        height: geometry.height,
        transform: `translateY(${geometry.top}px)`,
      }
    : undefined;

  return { indicatorStyle, rootRef: setRoot, shouldAnimate };
}
